import { Box, Button, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import { addChangesHistory, createTicket } from 'store/ticket-service/asyncActions';
import { ROUTES } from 'core/constants/routes';

import {
	getActiveHistoryItem,
	getActiveTicket,
	getHistoryMode,
	getIsPaymentMode,
	getIsReportAnIssueMode,
	getIsTariffPlansModalOpen,
	getIsTicketShare,
	getIsTicketUpdate,
	getPaymentModalInfo,
	getTicketsLoadingMore,
} from 'store/ticket-service/selector';
import {
	addLocalTicket,
	setActiveTicket,
	setIsTariffPlansModalOpen,
	setUpdatePaymentInfoModal,
} from 'store/ticket-service/ticketSlice';
import { TicketType } from 'store/ticket-service/types';
import { getIsCreateTicketShare } from 'store/ticket-share-service/selector';
import {
	createCustomer,
	createFreeSubscription,
	fetchUserBalance,
	getUserProfile,
	verifyUserEmail,
} from 'store/user-service/asyncActions';
import {
	getDeniedAccessError,
	getIsDeleteAccessModal,
	getIsExportModalOpen,
	getIsOpenUserProfile,
	getMethodStatus,
	getNotEnoughTokensError,
	getPaymentMethods,
	getPaymentStatus,
	getStripeInstance,
} from 'store/user-service/selector';
import {
	addLoadedUser,
	setIsOpenUserProfile,
	setStripeConnection,
	setStripeInstance,
} from 'store/user-service/userSlice';

import { flexMixin } from 'utils/flexMixin';

import { MainPageLayout } from 'layouts/MainPageLayout';

import { getEnvVarAsBoolean } from 'core/helpers/env-helper';
import { hub_events } from 'core/constants/hubEvents';
import {
	GeneralStepConversationTopics,
	HistoryActionTypes,
	MessageSender,
	NamespacesList,
	PaymentStatus,
	TicketStages,
} from 'core/enums/common';
import { successNotify } from 'core/helpers/toasts';
import { useGetPaymentMethods } from 'core/hooks/useGetPaymentMethods';
import { useGetUser } from 'core/hooks/useGetUser';
import usePaymentStream from 'core/hooks/usePaymentStream';
import { ISocketConnectionData, PaymentMethodType } from 'core/types/base';

import { DetailedChangesView } from 'components/ChangesVersioning';
import { IMessage } from 'components/Chatbot/Message/types';
import { NoPaymentMethodModal } from 'components/Payment/NoPaymentMethodModal';
import { PaymentCancelModal } from 'components/Payment/PaymentCancelModal';
import { PaymentModal } from 'components/Payment/PaymentModal';
import { PaymentSuccessModal } from 'components/Payment/PaymentSuccessModal';
import { NotEnoughTokensModal } from 'components/Payment/notEnoughTokensModal';
import { Profile } from 'components/Profile';
import { DeniedAccessModal } from 'components/Profile/DeniedAccessModal';
import { PreparingUserModal } from 'components/Profile/PreparingUserModal';
import { TariffPlansModal } from 'components/Profile/Subscription/TariffPlansModal';
import { UpdatePaymentInformationModal } from 'components/Profile/Subscription/UpdatePaymentInformation';
import { MethodCancelModal } from 'components/Profile/Subscription/UpdatePaymentInformation/MethodCancelModal';
import { MethodSuccessModal } from 'components/Profile/Subscription/UpdatePaymentInformation/MethodSuccessModal';
import { ReportAnIssue } from 'components/ReportAnIssue';
import { CreateTicketModal } from 'components/Ticket/CreateTicketModal';
import { DeleteTicketModal } from 'components/Ticket/DeleteTicketModal';
import { TicketDescription } from 'components/Ticket/TicketDescription';
import { CreateTicketShareModal } from 'components/TicketShare/CreateTicketShareModal';
import { NoDeleteAccessModal } from 'components/TicketShare/NoDeleteAccessModal';
import { ExportModal } from 'components/TicketSteps/ReportStep/ExportModal';

import { ReactComponent as Add } from 'assets/icons/add.svg';
import { ReactComponent as Message } from 'assets/icons/message-text.svg';

export type ContentType = 'Chat' | 'RequestInfo';

export interface FormTitleValue {
	title: string;
}

export default function Home() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { t: commonTranslate, i18n } = useTranslation(NamespacesList['common']);
	const { t: modalsTranslate } = useTranslation(NamespacesList['modals']);

	const activeTicket = useSelector(getActiveTicket);
	const reportAnIssueMode = useSelector(getIsReportAnIssueMode);
	const paymentMode = useSelector(getIsPaymentMode);
	const updatePaymentModalInfo = useSelector(getPaymentModalInfo);
	const isTariffPlanModalOpen = useSelector(getIsTariffPlansModalOpen);
	const loading = useSelector(getTicketsLoadingMore);
	const isTicketUpdate = useSelector(getIsTicketUpdate);
	const paymentStatus = useSelector(getPaymentStatus);
	const methodStatus = useSelector(getMethodStatus);
	const notEnoughTokensError = useSelector(getNotEnoughTokensError);
	const isHistoryMode = useSelector(getHistoryMode);
	const activeHistoryItem = useSelector(getActiveHistoryItem);
	const isOpenUserProfile = useSelector(getIsOpenUserProfile);
	const stripeInstance = useSelector(getStripeInstance);
	const isOpenDeniedAccess = useSelector(getDeniedAccessError);
	const paymentMethods = useSelector(getPaymentMethods);
	const isCreateTicketShareOpen = useSelector(getIsCreateTicketShare);
	const isExportModalOpen = useSelector(getIsExportModalOpen);
	const isOpenDeleteAccessModal = useSelector(getIsDeleteAccessModal);

	const { openConnection, closeConnection } = usePaymentStream();
	const [searchParams, setSearchParams] = useSearchParams();
	const params = useParams<{ email?: string; token?: string }>();
	const { gettingPaymentLoading, handleGetAllPaymentsMethods } = useGetPaymentMethods();
	const { user } = useGetUser();

	const isTicketShare = useSelector(getIsTicketShare);

	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
	const [isShowSaveModal, setIsShowSaveModal] = useState<boolean>(false);
	const [startModalOpen, setStartModalOpen] = useState<boolean>(false);
	const [preparingUser, setPreparingUser] = useState<boolean>(false);

	const handleActionStartModal = () => {
		setStartModalOpen((prev) => !prev);
	};

	useEffect(() => {
		dispatch(fetchUserBalance());
	}, []);

	const handleCloseModal = () => {
		setIsShowSaveModal(false);
		setIsDeleteModalOpen(false);
	};

	const createLocalTicket = async () => {
		const welcomeMessage: IMessage = {
			content: `generalStep.welcomeMessage.${GeneralStepConversationTopics[0]}`,
			sender: MessageSender.System,
			sendTime: new Date().toISOString(),
			stage: TicketStages.general,
			subStage: GeneralStepConversationTopics['Functional requirements'],
			userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
		};

		const defaultTicket: Partial<TicketType> = {
			title: 'New request',
			description: '',
			createdDate: new Date().toISOString(),
			comment: '',
			context: '',
			messages: [welcomeMessage],
			status: 0,
			currentStep: TicketStages.general,
			ticketSummaries: [],
			userStoryTests: [],
			useCases: [],
			notifications: [],
		};

		dispatch(createTicket({ userId: user?.id || '', ticket: defaultTicket })).then((data: any) => {
			dispatch(addLocalTicket(data.payload));
			dispatch(setActiveTicket(data.payload));

			dispatch(
				addChangesHistory({
					userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
					ticketId: data.payload.id,
					ticketStep: TicketStages.general,
					actionType: HistoryActionTypes.ticketCreate,
				})
			);
		});
	};

	useEffect(() => {
		dispatch(addLoadedUser(user));
	}, [user]);

	const handleUpdatePaymentInfoModalClose = () => {
		dispatch(setUpdatePaymentInfoModal({ ...updatePaymentModalInfo, isOpen: false }));
	};

	const handleTariffPlanModalClose = () => dispatch(setIsTariffPlansModalOpen(false));

	const isTicketSaveAvailable = useMemo(() => {
		if (isTicketShare) {
			const ticketShareStagesPermissions =
				activeTicket?.permissions?.map((permission) => permission.ticketCurrentStep) || [];

			return (
				ticketShareStagesPermissions.includes(TicketStages.general) ||
				ticketShareStagesPermissions.includes(TicketStages.notifications) ||
				ticketShareStagesPermissions.includes(null)
			);
		}

		return true;
	}, [isTicketShare, activeTicket?.id]);

	const renderMainContent = useMemo(() => {
		if (isOpenUserProfile) {
			return <Profile />;
		} else if (activeTicket) {
			if (reportAnIssueMode) {
				return <ReportAnIssue />;
			}
			if (isHistoryMode) {
				return <DetailedChangesView currentHistoryItem={activeHistoryItem} />;
			}
			if (isCreateTicketShareOpen) {
				return <CreateTicketShareModal />;
			}
			return (
				<TicketDescription
					setIsDeleteModalOpen={setIsDeleteModalOpen}
					setIsShowSaveModal={setIsShowSaveModal}
				/>
			);
		}

		return !loading ? (
			<Box
				sx={{
					...flexMixin({ direction: 'column' }),
					width: '100%',
				}}
			>
				<Box
					sx={{
						textAlign: 'center',
						maxWidth: '473px',
						width: '100%',
					}}
				>
					<Message style={{ marginBottom: '24px' }} />
					<Typography
						sx={{
							fontSize: '20px',
							fontWeight: 700,
							marginBottom: '8px',
						}}
					>
						{commonTranslate('text.homePageNoTicketsTitle')}
					</Typography>
					<Typography sx={{ marginBottom: '48px' }}>
						{commonTranslate('text.homePageNoTicketsDescription')}
					</Typography>
					<Button
						sx={{
							height: '44px',
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: '700',
							gap: '8px',
						}}
						variant="outlined"
						color="primary"
						onClick={createLocalTicket}
					>
						<Add />
						{commonTranslate('buttons.createNewRequest')}
					</Button>
				</Box>
			</Box>
		) : (
			<Box
				sx={{
					...flexMixin({ direction: 'column' }),
					width: '100%',
				}}
			>
				<Oval
					height={24}
					width={24}
					color="#fff"
					wrapperStyle={{}}
					wrapperClass=""
					visible={true}
					ariaLabel="oval-loading"
					secondaryColor="#91B6FF"
					strokeWidth={5}
					strokeWidthSecondary={5}
				/>
			</Box>
		);
	}, [
		activeTicket,
		reportAnIssueMode,
		isHistoryMode,
		loading,
		isOpenUserProfile,
		activeHistoryItem,
		isCreateTicketShareOpen,
		i18n.language,
	]);

	const handleCloseConnection = () => {
		if (stripeInstance) {
			closeConnection(stripeInstance?.connection);
			dispatch(setStripeInstance(null));
		}
	};

	useEffect(() => {
		handleCloseConnection();

		const isPaymentEnabled = getEnvVarAsBoolean('REACT_APP_ENABLE_PAYMENTS', true);

		if (isPaymentEnabled) {
			openConnection().then(async (connection: ISocketConnectionData) => {
				connection.connection.on(hub_events.action.addingPaymentMethod, (message) => {
					dispatch(setStripeConnection(false));
					window.open(message, '_self');
				});

				connection.connection.on(hub_events.action.freeSubscription, () => {
					dispatch(setStripeConnection(false));
					setPreparingUser(false);
					dispatch(getUserProfile());
				});

				connection?.connection.on(hub_events.response.paymentSuccess, () => {
					dispatch(fetchUserBalance());
				});

				connection.connection.on(
					hub_events.action.successfulPaymentSignalRMethodName,
					(message) => {
						dispatch(setStripeConnection(false));
						window.open(message, '_self');
					}
				);

				connection.connection.on(hub_events.response.customerCreated, () => {});

				if (connection.connectionId) {
					try {
						const userData = await dispatch(getUserProfile()).unwrap();
						const isPaymentEnabled = getEnvVarAsBoolean('REACT_APP_ENABLE_PAYMENTS', true);

						if (userData && isPaymentEnabled) {
							if (
								userData?.stripeCustomerId === null ||
								userData?.stripeCustomerId?.length === 0 ||
								userData.accountPlan === null
							) {
								setPreparingUser(true);
								handleActionStartModal();
							}
							if (userData?.stripeCustomerId === null || userData?.stripeCustomerId.length === 0) {
								await dispatch(createCustomer()).unwrap();
							}
							if (userData.accountPlan === null) {
								await dispatch(
									createFreeSubscription({
										connectionId: connection.connectionId,
										signalMethodName: hub_events.action.freeSubscription,
									})
								).unwrap();
							}
						}
					} catch (error: any) {
						setPreparingUser(false);
						console.log(error);
					}
				}

				dispatch(setStripeInstance(connection));
			});
		}
	}, []);

	const handleVerifyEmail = async () => {
		// Priorité aux path parameters, sinon fallback sur query parameters pour compatibilité
		const email = params.email ? decodeURIComponent(params.email) : searchParams.get('email');
		const token = params.token || searchParams.get('token');
		
		if (email && token) {
			try {
				await dispatch(setIsOpenUserProfile(true));
				await dispatch(
					verifyUserEmail({
						token: token,
						userEmail: email,
					})
				).unwrap();
				await successNotify(
					modalsTranslate('successVerifyEmail.title'),
					modalsTranslate('successVerifyEmail.profileDescription')
				);
				// Si on utilise les path params, rediriger vers /brd sans paramètres
				if (params.email && params.token) {
					navigate(ROUTES.BRD, { replace: true });
				} else {
					// Sinon, nettoyer les query params
					searchParams.delete('token');
					searchParams.delete('email');
					setSearchParams(searchParams);
				}
			} catch (error) {
				console.log(error);
			}
		}
	};
	useEffect(() => {
		handleVerifyEmail();
	}, []);

	const hasDefaultPaymentMethod = paymentMethods?.some((item: PaymentMethodType) => item.default);

	return (
		<MainPageLayout>
			<Box
				sx={{
					height: '100%',
					background: '#fff',
					padding: { xs: '12px', sm: '16px', md: '20px', xl: '24px' },
					borderRadius: { xs: '8px', md: '0 8px 8px 0' },
					display: 'flex',
					flexDirection: 'column',
					justifyContent: loading || !activeTicket ? 'center' : 'space-between',
					gap: { xs: '8px', sm: '12px', md: '12px', xl: '24px' },
					boxShadow: '0px 8px 24px 0px rgba(40, 103, 131, 0.08)',
					overflow: 'hidden',
					width: '100%',
					boxSizing: 'border-box',
				}}
			>
				{renderMainContent}

				{isShowSaveModal && isTicketUpdate && activeTicket && isTicketSaveAvailable ? (
					<CreateTicketModal
						handleCloseModal={handleCloseModal}
						ticket={activeTicket}
						userId={user?.id || ''}
					/>
				) : null}
				{paymentMode ? <PaymentModal /> : null}
				{updatePaymentModalInfo.isOpen ? (
					<UpdatePaymentInformationModal
						previousUrlQuery={updatePaymentModalInfo.previousUrlQuery}
						isOpen={updatePaymentModalInfo.isOpen}
						onClose={handleUpdatePaymentInfoModalClose}
						gettingPaymentsLoading={gettingPaymentLoading}
						paymentMethods={updatePaymentModalInfo.paymentMethods}
						getPaymentsMethods={handleGetAllPaymentsMethods}
					/>
				) : null}
				{}
				<TariffPlansModal
					isOpen={isTariffPlanModalOpen}
					onClose={handleTariffPlanModalClose}
					isOnPurchase={true}
				/>
				{paymentStatus === PaymentStatus.success ? <PaymentSuccessModal /> : null}
				{paymentStatus === PaymentStatus.cancel ? <PaymentCancelModal /> : null}
				{methodStatus === PaymentStatus.success ? <MethodSuccessModal /> : null}
				{methodStatus === PaymentStatus.cancel ? <MethodCancelModal /> : null}
				{notEnoughTokensError &&
					(hasDefaultPaymentMethod ? <NotEnoughTokensModal /> : <NoPaymentMethodModal />)}
				{isOpenDeniedAccess ? <DeniedAccessModal /> : null}
				{isExportModalOpen ? <ExportModal /> : null}
				{isOpenDeleteAccessModal ? <NoDeleteAccessModal /> : null}

				{isDeleteModalOpen && (
					<DeleteTicketModal
						handleCloseModal={handleCloseModal}
						ticketId={activeTicket?.id}
						userId={user?.id || ''}
					/>
				)}
				<PreparingUserModal
					isOpen={startModalOpen}
					onClose={handleActionStartModal}
					isPreparing={preparingUser}
				/>
			</Box>
		</MainPageLayout>
	);
}
